import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { updateProfile } from 'firebase/auth';
import { db, auth } from '../firebase';
import Avatar from './Avatar';
import '../styles/global.css';
import '../styles/Profile.css';
import LoadingSpinner from './LoadingSpinner';

const AVATAR_EMOJIS = ["😀", "🦁", "🐱", "🐶", "🐵", "🐸", "🐼", "🐧", "🦊", "🐻"];
const AVATAR_GRAPHICS = Array.from({ length: 21 }, (_, i) => `avatar_${i + 1}`);

const Profile = () => {
    const navigate = useNavigate();
    const [profile, setProfile] = useState({
        uid: '',
        displayName: '',
        photoUrl: '',
        bio: '',
        interests: [],
        isPremium: false,
        premiumEndDate: null,
        avatar: '👤',
        createdAt: null,
        lastOperationalUpdate: null,
        universidadNombre: '',
        universidadAbreviatura: '',
        carreraNombre: '',
        campus: '',
        modulosRequeridos: []
    });
    const [isEditing, setIsEditing] = useState(false);
    const [isSaving, setIsSaving] = useState(false);
    const [error, setError] = useState('');
    const [success, setSuccess] = useState('');
    const [loading, setLoading] = useState(true);
    const [showAvatarSelector, setShowAvatarSelector] = useState(false);

    const formatDate = (timestamp) => {
        if (timestamp === 0) return 'Acceso Permanente';
        if (!timestamp) return 'S/I';
        // Handle Firestore Timestamp, Date object, or milliseconds
        let date;
        if (timestamp.toDate) {
            date = timestamp.toDate();
        } else if (timestamp instanceof Date) {
            date = timestamp;
        } else {
            date = new Date(timestamp);
        }

        return date.toLocaleDateString('es-ES', {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    useEffect(() => {
        loadProfile();
    }, []);

    const loadProfile = async () => {
        if (!auth.currentUser) {
            setLoading(false);
            return;
        }

        try {
            const userDoc = await getDoc(doc(db, 'users', auth.currentUser.uid));
            if (userDoc.exists()) {
                const userData = userDoc.data();
                setProfile({
                    uid: auth.currentUser.uid,
                    displayName: userData.nick || userData.displayName || auth.currentUser.displayName || '',
                    photoUrl: userData.photoUrl || auth.currentUser.photoURL || '',
                    avatar: userData.avatar || '👤',
                    bio: userData.bio || '',
                    interests: userData.interests || [],
                    isPremium: userData.isPremium || false,
                    premiumEndDate: userData.premiumEndDate || null,
                    createdAt: userData.createdAt || auth.currentUser.metadata.creationTime,
                    lastOperationalUpdate: userData.lastOperationalUpdate || null,
                    universidadNombre: userData.universidadNombre || '',
                    universidadAbreviatura: userData.universidadAbreviatura || '',
                    carreraNombre: userData.carreraNombre || '',
                    campus: userData.campus || userData.sede_principal || userData.sede || userData.plantel || (userData.universidadNombre ? 'Campus Central / Sede Principal' : ''),
                    modulosRequeridos: userData.modulosRequeridos || []
                });

            } else {
                setProfile({
                    uid: auth.currentUser.uid,
                    displayName: auth.currentUser.displayName || 'Usuario Vortex',
                    photoUrl: auth.currentUser.photoURL || '',
                    avatar: '👤',
                    bio: '',
                    interests: [],
                    isPremium: false,
                    premiumEndDate: null,
                    createdAt: auth.currentUser.metadata.creationTime,
                    universidadNombre: '',
                    universidadAbreviatura: '',
                    carreraNombre: '',
                    campus: '',
                    modulosRequeridos: []
                });
            }
        } catch (error) {
            console.error('Error loading profile:', error);
            setError('Error al cargar el perfil operativo');
        }
        setLoading(false);
    };

    const handleSave = async () => {
        if (!auth.currentUser) return;

        setIsSaving(true);
        setError('');
        setSuccess('');

        try {
            // Update Firebase Auth profile
            await updateProfile(auth.currentUser, {
                displayName: profile.displayName,
                photoURL: profile.photoUrl
            });

            // Update Firestore profile
            await setDoc(doc(db, 'users', auth.currentUser.uid), {
                nick: profile.displayName,
                photoUrl: profile.photoUrl,
                avatar: profile.avatar,
                bio: profile.bio,
                interests: profile.interests,
                updatedAt: new Date()
            }, { merge: true });

            setSuccess('Perfil sincronizado correctamente');
            setIsEditing(false);
        } catch (error) {
            console.error('Error saving profile:', error);
            setError('Error en la sincronización de datos');
        }
        setIsSaving(false);
    };

    const handleInterestAdd = (interest) => {
        if (interest.trim() && !profile.interests.includes(interest.trim())) {
            setProfile(prev => ({
                ...prev,
                interests: [...prev.interests, interest.trim()]
            }));
        }
    };

    const handleInterestRemove = (interest) => {
        setProfile(prev => ({
            ...prev,
            interests: prev.interests.filter(i => i !== interest)
        }));
    };

    const handleCancel = () => {
        loadProfile();
        setIsEditing(false);
        setError('');
        setSuccess('');
    };

    if (loading) {
        return (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '60vh' }}>
                   <LoadingSpinner label="Cargando datos..." height="300px" />
            </div>
        );
    }

    return (
        <div className="profile-wrapper" style={{ maxWidth: '100%', margin: 0, height: 'calc(100vh - 60px)', boxSizing: 'border-box', overflowY: 'auto' }}>
            <div className="profile-header-flex">
                <div>
                    <h1 className="profile-header-title">Ficha Operativa</h1>
                    <p className="profile-header-desc">Centro de control de identidad y estado de ascenso</p>
                </div>
                {!isEditing ? (
                    <button
                        onClick={() => setIsEditing(true)}
                        className="btn-primary"
                        style={{ padding: '12px 25px', borderRadius: '12px', fontWeight: '700' }}
                    >
                        Editar Perfil
                    </button>
                ) : (
                    <div className="flex gap-md">
                        <button
                            onClick={handleCancel}
                            disabled={isSaving}
                            className="btn-secondary"
                            style={{ padding: '12px 25px', borderRadius: '12px' }}
                        >
                            Cancelar
                        </button>
                        <button
                            onClick={handleSave}
                            disabled={isSaving}
                            className="btn-primary"
                            style={{ padding: '12px 25px', borderRadius: '12px', fontWeight: '700' }}
                        >
                            {isSaving ? 'Sincronizando...' : 'confirmar Cambios'}
                        </button>
                    </div>
                )}
            </div>

            {error && <div className="error-message" style={{ marginBottom: '20px' }}>⚠️ {error}</div>}
            {success && <div className="card" style={{ background: 'rgba(0, 242, 254, 0.1)', color: 'var(--accent)', padding: '1rem', textAlign: 'center', marginBottom: '1.5rem', border: '1px solid var(--accent)', borderRadius: '12px' }}>✅ {success}</div>}

            <div className="profile-grid">
                {/* Lateral Column - Profile Image and Main Status */}
                <div className="profile-sidebar" style={{ zIndex: showAvatarSelector ? 100 : 1 }}>
                    <div className="card profile-sidebar-card">
                        {profile.isPremium && (
                            <div className="premium-badge">
                                PREMIUM ⭐
                            </div>
                        )}

                        <div className="avatar-container">
                            <Avatar src={profile.avatar} size={150} />

                            {isEditing && (
                                <button
                                    className="edit-avatar-btn"
                                    onClick={() => setShowAvatarSelector(!showAvatarSelector)}
                                >
                                    ✏️
                                </button>
                            )}
                        </div>

                        {showAvatarSelector && isEditing && (
                            <div className="avatar-selector-panel">
                                <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                                    <h4 style={{ margin: 0, color: 'var(--accent)', fontSize: '0.9rem' }}>Elige tu Identidad</h4>
                                    <button onClick={() => setShowAvatarSelector(false)} style={{ background: 'none', border: 'none', color: 'white', cursor: 'pointer' }}>✕</button>
                                </div>

                                <div className="avatar-scroll">
                                    {AVATAR_GRAPHICS.map(av => (
                                        <div
                                            key={av}
                                            onClick={() => {
                                                setProfile({ ...profile, avatar: av });
                                                setShowAvatarSelector(false);
                                            }}
                                            className={`avatar-item ${profile.avatar === av ? 'selected' : ''}`}
                                        >
                                            <Avatar src={av} size={45} />
                                        </div>
                                    ))}
                                </div>

                                <div className="emoji-scroll">
                                    {AVATAR_EMOJIS.map(emoji => (
                                        <span
                                            key={emoji}
                                            onClick={() => {
                                                setProfile({ ...profile, avatar: emoji });
                                                setShowAvatarSelector(false);
                                            }}
                                            className={`emoji-item ${profile.avatar === emoji ? 'selected' : ''}`}
                                        >
                                            {emoji}
                                        </span>
                                    ))}
                                </div>
                            </div>
                        )}

                        <h2 style={{ fontSize: '1.5rem', marginBottom: '5px' }}>{profile.displayName || 'Vortex User'}</h2>
                        <div className="profile-stats-list">
                            <div className="profile-stat-row">
                                <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>ID de Usuario</span>
                                <code style={{ fontSize: '0.7rem', opacity: 0.5 }}>{profile.uid.substring(0, 10)}...</code>
                            </div>
                            <div className="profile-stat-row">
                                <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Universidad</span>
                                <span style={{ fontSize: '0.8rem', fontWeight: '600', color: '#c5a059', textAlign: 'right', maxWidth: '60%' }}>
                                    {profile.universidadNombre || profile.universidadAbreviatura || 'Sin seleccionar'}
                                </span>
                            </div>
                            <div className="profile-stat-row">
                                <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Carrera</span>
                                <span style={{ fontSize: '0.8rem', fontWeight: '600', color: '#38bdf8', textAlign: 'right', maxWidth: '60%' }}>
                                    {profile.carreraNombre || 'Sin seleccionar'}
                                </span>
                            </div>
                            <div className="profile-stat-row">
                                <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Campus / Sede</span>
                                <span style={{ fontSize: '0.8rem', fontWeight: '600', textAlign: 'right', maxWidth: '60%' }}>
                                    {profile.campus || 'Sede Principal'}
                                </span>
                            </div>
                            <div className="profile-stat-row">
                                <span style={{ fontSize: '0.8rem', color: 'var(--text-secondary)' }}>Antigüedad</span>
                                <span style={{ fontSize: '0.8rem', fontWeight: '600' }}>{formatDate(profile.createdAt)}</span>
                            </div>
                            {profile.isPremium && (
                                <div className="profile-stat-row">
                                    <span style={{ fontSize: '0.8rem', color: 'gold', fontWeight: '700' }}>Expira</span>
                                    <span style={{ fontSize: '0.8rem', fontWeight: '600', color: 'gold' }}>{formatDate(profile.premiumEndDate)}</span>
                                </div>
                            )}
                        </div>

                        <a
                            href="https://billing.stripe.com/p/login/dRm28r45W4Kmeo2bHz73G00"
                            target="_blank"
                            rel="noopener noreferrer"
                            className="btn-primary"
                            style={{ display: 'block', textAlign: 'center', textDecoration: 'none', marginTop: '12px', fontSize: '0.8rem' }}
                        >
                            Cancelar suscripción
                        </a>
                    </div>
                </div>

                {/* Main Content - Personal Info and Settings */}
                <div className="card profile-main-card">
                    <div style={{ display: 'grid', gap: '2rem' }}>

                        {/* Información Académica EXANI-II */}
                        <div style={{
                            background: 'linear-gradient(135deg, rgba(197, 160, 89, 0.08) 0%, rgba(13, 32, 53, 0.6) 100%)',
                            border: '1px solid rgba(197, 160, 89, 0.3)',
                            borderRadius: '12px',
                            padding: '18px',
                            display: 'flex',
                            flexDirection: 'column',
                            gap: '12px'
                        }}>
                     

                            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '12px' }}>
                                <div>
                                    <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', display: 'block', textTransform: 'uppercase' }}>Universidad</span>
                                    <strong style={{ fontSize: '0.95rem', color: '#c5a059' }}>{profile.universidadNombre || profile.universidadAbreviatura || 'No seleccionada'}</strong>
                                </div>
                                <div>
                                    <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', display: 'block', textTransform: 'uppercase' }}>Carrera</span>
                                    <strong style={{ fontSize: '0.95rem', color: '#38bdf8' }}>{profile.carreraNombre || 'No seleccionada'}</strong>
                                </div>
                                <div>
                                    <span style={{ fontSize: '0.75rem', color: 'var(--text-secondary)', display: 'block', textTransform: 'uppercase' }}>Campus / Sede</span>
                                    <strong style={{ fontSize: '0.95rem', color: '#e2e8f0' }}>{profile.campus || 'Sede Principal'}</strong>
                                </div>
                            </div>
                        </div>

                        {/* Display Name Input */}
                        <div className="form-group">
                            <label style={{ color: 'var(--text-secondary)', fontWeight: '700', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '10px', display: 'block' }}>
                                Nickname / Alias de Red
                            </label>
                            {isEditing ? (
                                <input
                                    type="text"
                                    value={profile.displayName}
                                    onChange={(e) => setProfile(prev => ({ ...prev, displayName: e.target.value }))}
                                    className="form-input"
                                    style={{
                                        width: '100%',
                                        padding: '15px',
                                        background: 'rgba(255,255,255,0.05)',
                                        border: '1px solid var(--accent)',
                                        color: 'white',
                                        borderRadius: '12px',
                                        fontSize: '1rem'
                                    }}
                                />
                            ) : (
                                <div style={{ fontSize: '1.2rem', fontWeight: '500' }}>
                                    {profile.displayName || 'Anónimo'}
                                </div>
                            )}
                        </div>

                        {/* Bio Input */}
                        <div className="form-group">
                            <label style={{ color: 'var(--text-secondary)', fontWeight: '700', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '10px', display: 'block' }}>
                                Estrategia Personal / Bio
                            </label>
                            {isEditing ? (
                                <textarea
                                    value={profile.bio}
                                    onChange={(e) => setProfile(prev => ({ ...prev, bio: e.target.value }))}
                                    rows={4}
                                    placeholder="Describa su enfoque operativo..."
                                    style={{
                                        width: '100%',
                                        padding: '15px',
                                        background: 'rgba(255,255,255,0.05)',
                                        border: '1px solid var(--accent)',
                                        color: 'white',
                                        borderRadius: '12px',
                                        fontSize: '1rem',
                                        resize: 'none',
                                        lineHeight: '1.5'
                                    }}
                                />
                            ) : (
                                <p style={{ margin: 0, fontSize: '1rem', color: 'var(--text-secondary)', lineHeight: '1.6' }}>
                                    {profile.bio || 'Sin datos biográficos registrados en el sistema.'}
                                </p>
                            )}
                        </div>

                        {/* Interests Selection */}
                        <div className="form-group">
                            <label style={{ color: 'var(--text-secondary)', fontWeight: '700', fontSize: '0.75rem', textTransform: 'uppercase', letterSpacing: '1px', marginBottom: '10px', display: 'block' }}>
                                Especializaciones de Interés
                            </label>
                            {isEditing ? (
                                <div>
                                    <div className="flex gap-md" style={{ marginBottom: '15px' }}>
                                        <input
                                            type="text"
                                            placeholder="Nueva especialización..."
                                            onKeyPress={(e) => {
                                                if (e.key === 'Enter') {
                                                    handleInterestAdd(e.target.value);
                                                    e.target.value = '';
                                                }
                                            }}
                                            style={{
                                                flex: 1,
                                                padding: '12px',
                                                background: 'rgba(255,255,255,0.05)',
                                                border: '1px solid var(--border)',
                                                color: 'white',
                                                borderRadius: '10px'
                                            }}
                                        />
                                        <button
                                            onClick={(e) => {
                                                const input = e.currentTarget.previousElementSibling;
                                                handleInterestAdd(input.value);
                                                input.value = '';
                                            }}
                                            className="btn-primary"
                                            style={{ padding: '10px 20px', fontSize: '0.8rem' }}
                                        >
                                            Añadir
                                        </button>
                                    </div>
                                    <div className="flex flex-wrap gap-md">
                                        {profile.interests.map((interest, index) => (
                                            <div
                                                key={index}
                                                className="interest-tag"
                                            >
                                                {interest}
                                                <span
                                                    onClick={() => handleInterestRemove(interest)}
                                                    style={{ cursor: 'pointer', fontSize: '1.2rem', fontWeight: '400' }}
                                                >
                                                    ×
                                                </span>
                                            </div>
                                        ))}
                                    </div>
                                </div>
                            ) : (
                                <div className="flex flex-wrap gap-md">
                                    {profile.interests.length > 0 ? (
                                        profile.interests.map((interest, index) => (
                                            <span
                                                key={index}
                                                className="interest-tag-display"
                                            >
                                                {interest}
                                            </span>
                                        ))
                                    ) : (
                                        <span style={{ color: 'var(--text-muted)', fontSize: '0.9rem', fontStyle: 'italic' }}>No se han registrado especializaciones de interés.</span>
                                    )}
                                </div>
                            )}
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default Profile;